import React, { useState, useEffect } from "react";
import { AlertTriangle, Info, Loader2, Trash2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PopupTransition from "@/lib/components/ui/PopupTransition";

interface ConfirmationModalProps {
    isOpen: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type?: "danger" | "warning" | "info";
    onConfirm: () => void;
    isLoading?: boolean;
    onCancel?: () => void;
    validationString?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    setIsOpen,
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "danger",
    onConfirm,
    isLoading = false,
    validationString,
    onCancel
}) => {
    const [inputValue, setInputValue] = useState("");

    // Reset input when dialog opens
    useEffect(() => {
        if (isOpen) setInputValue("");
    }, [isOpen]);

    const isConfirmDisabled = validationString
        ? inputValue !== validationString
        : false;

    // Config based on type (Light Mode Optimized for enexan-portal)
    const config = {
        danger: {
            icon: <Trash2 className="w-6 h-6 text-red-500" />,
            bgColor: "bg-red-50",
            buttonColor: "bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/20",
            borderColor: "border-red-100",
            focusRing: "focus:border-red-500 focus:ring-red-500",
        },
        warning: {
            icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
            bgColor: "bg-amber-50",
            buttonColor: "bg-amber-500 hover:bg-amber-600 shadow-lg shadow-amber-500/20",
            borderColor: "border-amber-100",
            focusRing: "focus:border-amber-500 focus:ring-amber-500",
        },
        info: {
            icon: <Info className="w-6 h-6 text-blue-500" />,
            bgColor: "bg-blue-50",
            buttonColor: "bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20",
            borderColor: "border-blue-100",
            focusRing: "focus:border-blue-500 focus:ring-blue-500",
        },
    }[type];

    const handleClose = () => {
        if (!isLoading) setIsOpen?.(false);
        onCancel?.();
    };

    // Since enexan-portal might not have the same PopupTransition, we fallback to AnimatePresence if needed
    // But let's assume PopupTransition exists or we just inline the AnimatePresence modal
    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm"
                    />
                    
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden relative z-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-0"
                        >
                            <X size={18} />
                        </button>

                        <div className="p-6 pt-8 text-center">
                            {/* Icon */}
                            <div
                                className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 border ${config.bgColor} ${config.borderColor}`}
                            >
                                {config.icon}
                            </div>

                            <h3 className="text-xl font-bold text-gray-900 mb-3">
                                {title}
                            </h3>

                            <p className="text-gray-500 text-sm leading-relaxed mb-6">
                                {message}
                            </p>

                            {/* Validation Input Section */}
                            {validationString && (
                                <div className="mb-8">
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                                        Type <span className="text-red-600 select-none mx-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-100">{validationString}</span> to confirm
                                    </label>
                                    <input
                                        type="text"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        placeholder={validationString}
                                        disabled={isLoading}
                                        className={`w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-center font-bold text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:outline-none focus:ring-1 ${config.focusRing} transition-all`}
                                    />
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={handleClose}
                                    disabled={isLoading}
                                    className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-50"
                                >
                                    {cancelText}
                                </button>
                                <button
                                    onClick={onConfirm}
                                    disabled={isLoading || isConfirmDisabled}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white transition-all ${config.buttonColor} disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {isLoading ? "Processing..." : confirmText}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ConfirmationModal;
